import React, { useState } from 'react';
import { canvasService } from '../../api/services';
import useApi from '../../hooks/useApi';
import { X, Loader2, AlertCircle, FileText, ExternalLink, CheckCircle2, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_STYLES = {
  SUBMITTED:          { bg: 'bg-blue-50',  text: 'text-blue-700',  label: 'Awaiting Review' },
  GRADED:             { bg: 'bg-forest-50',text: 'text-forest-700',label: 'Graded'          },
  REVISION_REQUESTED: { bg: 'bg-rust-50',  text: 'text-rust-500',  label: 'Revision Requested' },
  PENDING:            { bg: 'bg-cream-100',text: 'text-forest-500',label: 'Not Submitted'  },
};

const fileLabel = (url) => {
  try { return decodeURIComponent(url.split('/').pop().split('?')[0]); }
  catch { return url; }
};

const CanvasSubmissionModal = ({ submissionId, onClose, onGraded }) => {
  const { data, loading, error, refetch } = useApi(() => canvasService.getSubmissionAdmin(submissionId), []);
  const submission = data?.submission ?? data ?? null;

  const [score, setScore] = useState('');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);

  const activity = submission?.canvasActivity;
  const level    = activity?.stage?.level;
  const client   = submission?.clientProfile?.user;
  const maxScore = activity?.maxScore ?? 100;
  const canReview = submission?.status === 'SUBMITTED';

  const handleGrade = async (forceStatus) => {
    const scoreNum = Number(score);
    if (score === '' || Number.isNaN(scoreNum) || scoreNum < 0) {
      toast.error('Enter a score first'); return;
    }
    if (scoreNum > maxScore) { toast.error(`Score cannot exceed ${maxScore}`); return; }
    if (forceStatus === 'REVISION_REQUESTED' && !feedback.trim()) {
      toast.error('Add feedback so the client knows what to fix'); return;
    }

    setSaving(true);
    try {
      await canvasService.gradeSubmissionAdmin(submissionId, {
        score: scoreNum, feedback: feedback.trim() || undefined,
        // Only send an explicit status for the "force revision" path —
        // otherwise let the backend decide pass/fail from score vs
        // the activity's passing threshold.
        ...(forceStatus && { status: forceStatus }),
      });
      toast.success(forceStatus === 'REVISION_REQUESTED' ? 'Sent back to client for revision' : 'Submission graded');
      onGraded?.();
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save review');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">

        <div className="flex items-start justify-between px-6 py-4 border-b border-cream-100 flex-shrink-0">
          <div>
            <h3 className="font-serif text-lg font-semibold text-forest-900">Canvas Submission</h3>
            {activity && (
              <p className="text-sm text-forest-600 mt-0.5">
                {activity.title} — {level?.name} · {activity.stage?.name}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 flex-shrink-0"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-forest-600" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={15} /> Could not load this submission.
            </div>
          )}

          {!loading && submission && (
            <>
              {/* Client + status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-forest-900">
                    {client ? `${client.firstName} ${client.lastName}` : 'Unknown client'}
                  </p>
                  <p className="text-xs text-forest-400">{client?.email}</p>
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[submission.status]?.bg} ${STATUS_STYLES[submission.status]?.text}`}>
                  {STATUS_STYLES[submission.status]?.label ?? submission.status}
                </span>
              </div>

              {/* Submission text */}
              {submission.submissionText && (
                <div>
                  <p className="text-xs font-semibold text-forest-500 uppercase tracking-wider mb-1.5">Submission</p>
                  <p className="text-sm text-forest-800 whitespace-pre-wrap bg-cream-50 border border-cream-200 rounded-xl p-4">
                    {submission.submissionText}
                  </p>
                </div>
              )}

              {/* Files */}
              {submission.fileUrls?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-forest-500 uppercase tracking-wider mb-1.5">Attached Files</p>
                  <div className="space-y-1.5">
                    {submission.fileUrls.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-forest-700 hover:text-forest-900 bg-cream-50 border border-cream-200 rounded-lg px-3 py-2 transition-colors">
                        <FileText size={14} className="flex-shrink-0" />
                        <span className="truncate flex-1">{fileLabel(url)}</span>
                        <ExternalLink size={12} className="flex-shrink-0 text-forest-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Already reviewed — show the verdict */}
              {submission.status !== 'SUBMITTED' && (
                <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 space-y-1">
                  <p className="text-sm text-forest-800">
                    <span className="font-semibold">Score:</span> {submission.score ?? '—'}/{maxScore}
                  </p>
                  {submission.feedback && (
                    <p className="text-sm text-forest-700"><span className="font-semibold">Feedback:</span> {submission.feedback}</p>
                  )}
                  {submission.reviewer && (
                    <p className="text-xs text-forest-400">
                      Reviewed by {submission.reviewer.firstName} {submission.reviewer.lastName}
                    </p>
                  )}
                </div>
              )}

              {/* Review form — only while awaiting review */}
              {canReview && (
                <div className="border-t border-cream-100 pt-5 space-y-3">
                  <p className="text-xs font-semibold text-forest-500 uppercase tracking-wider">Review</p>
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">Score (out of {maxScore})</label>
                    <input type="number" min={0} max={maxScore} value={score}
                      onChange={(e) => setScore(e.target.value)}
                      className="w-32 px-3.5 py-2 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">
                      Feedback {' '}<span className="text-forest-400">(required if requesting revision)</span>
                    </label>
                    <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3}
                      placeholder="What did they do well? What needs to change?"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 resize-none" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => handleGrade()} disabled={saving}
                      className="flex items-center gap-1.5 text-sm font-medium bg-forest-800 text-cream-50 px-4 py-2 rounded-full hover:bg-forest-900 transition-colors disabled:opacity-50">
                      <CheckCircle2 size={14} /> Submit Grade
                    </button>
                    <button onClick={() => handleGrade('REVISION_REQUESTED')} disabled={saving}
                      className="flex items-center gap-1.5 text-sm font-medium border border-rust-300 text-rust-500 px-4 py-2 rounded-full hover:bg-rust-50 transition-colors disabled:opacity-50">
                      <RotateCcw size={14} /> Request Revision
                    </button>
                  </div>
                  <p className="text-xs text-forest-400">
                    "Request Revision" sends this back to the client with your feedback and lets them resubmit.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CanvasSubmissionModal;